import { RefreshTokenLinkGuard } from '@guards/refresh-token-link.guard';
import { Router, Routes } from '@angular/router';
import { PAGES_ROUTES } from './pages/pages-routes';
import { ExternalRedirectGuard } from '@guards/external-redirect.guard';
import { DEPRECATED_PAGES_ROUTES } from './pages/deprecated-pages-routes';
import { PROGRAM_LANDING_ROUTES } from './pages/program-landing/program-landing.routes';
import { AuthorizationGuard } from './auth/guards/authorization.guard';
import { SaleGuard } from '@guards/sale.guard';
import { SaleMatcher } from './shared/matchers/sale.matcher';
import { WelcomeGuard } from '@guards/welcome.guard';
import { AffiliateLandingGuard } from './affiliate/guards/affiliate-landing.guard';
import { shortInviteRedirector } from './dashboard/competitions/competitions-routes';
import { inject } from '@angular/core';
import { LicenseCanonicalRedirectGuard } from '@guards/license-canonical-redirect.guard';
import { UpgradePaywallGuard } from '@payment/guards';
import { PricesResolver } from './resolvers/prices-resolver/prices-resolve.service';

const APP_ROUTE_DEFINITIONS: Routes = [
  {
    path: '',
    redirectTo: '/app/home',
    pathMatch: 'full',
  },

  // PUSH LINKS
  {
    path: 'app/movement-break-editor',
    canActivate: [
      async () =>
        await inject(Router).navigate(['/app/home'], {
          queryParams: { action: 'open-movement-break-editor' },
        }),
    ],
    children: [],
  },
  {
    path: 'app/movement-break-class',
    canActivate: [
      async () =>
        await inject(Router).navigate(['/app/home'], {
          queryParams: { action: 'open-movement-break-player' },
        }),
    ],
    children: [],
  },

  // LICENSE
  {
    path: 'app/license/activate/:id',
    canActivate: [LicenseCanonicalRedirectGuard],
    children: [],
  },
  {
    path: 'app/license/:type/activate/:id',
    canActivate: [LicenseCanonicalRedirectGuard],
    children: [],
  },
  {
    path: 'app/license/:type/activate',
    canActivate: [LicenseCanonicalRedirectGuard],
    children: [],
  },
  {
    path: 'license',
    loadComponent: () => import('./auth/auth.component').then(m => m.AuthComponent),
    children: [
      {
        path: 'activate/:id',
        loadComponent: () =>
          import('./auth/license-activation/license-activation.component').then(
            m => m.LicenseActivationComponent
          ),
      },
      {
        path: ':type/activate/:id',
        loadComponent: () =>
          import('./auth/license-activation/license-activation.component').then(
            m => m.LicenseActivationComponent
          ),
      },
      {
        path: ':type/activate',
        loadComponent: () =>
          import('./auth/license-activation/license-activation.component').then(
            m => m.LicenseActivationComponent
          ),
      },
    ],
  },

  // MAIN APP
  {
    path: 'app',
    loadChildren: () => import('./dashboard/dashboard-routes').then(m => m.DASHBOARD_ROUTES),
  },

  // REDEEM
  {
    path: 'redeem',
    loadComponent: () => import('./dashboard/redeem/redeem.component').then(m => m.RedeemComponent),
  },
  {
    path: 'influencer_flow',
    loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
    loadChildren: () =>
      import('./dashboard/redeem/influencer-flow-routes').then(m => m.INFLUENCER_FLOW_ROUTES),
    data: {
      disableSalePopup: true,
    },
  },

  // SALE
  {
    path: 'sale',
    canActivate: [SaleGuard],
    children: [],
  },
  {
    matcher: SaleMatcher,
    canActivate: [SaleGuard],
    children: [],
  },
  {
    path: 'app/sales/popup',
    loadComponent: () =>
      import('./payment/components/upgrade-paywall/upgrade-paywall.component').then(
        m => m.UpgradePaywallComponent
      ),
    canActivate: [UpgradePaywallGuard],
    resolve: { price: PricesResolver },
  },

  // WELCOME
  {
    path: 'welcome',
    canActivate: [WelcomeGuard],
    children: [],
  },

  // AUTH
  {
    path: 'auth',
    loadComponent: () => import('./auth/auth.component').then(m => m.AuthComponent),
    loadChildren: () => import('./auth/auth-routes').then(m => m.AUTH_ROUTES),
    canActivate: [AuthorizationGuard],
    runGuardsAndResolvers: 'always',
  },

  // PAYMENT
  {
    path: 'payment',
    loadChildren: () => import('./payment/payment-routes').then(m => m.PAYMENT_ROUTES),
  },

  // AFFILIATE
  {
    path: 'affiliate',
    canActivate: [AffiliateLandingGuard],
    loadChildren: () => import('./affiliate/affiliate.routes').then(m => m.AFFILIATE_ROUTES),
  },

  // CERTIFICATE
  {
    path: 'gift',
    loadComponent: () =>
      import('./pages/gift-certificate/gift.component').then(m => m.GiftComponent),
    loadChildren: () =>
      import('./pages/gift-certificate/gift-certificate-routes').then(
        m => m.GIFT_CERTIFICATE_ROUTES
      ),
  },

  ...PAGES_ROUTES,
  ...PROGRAM_LANDING_ROUTES,
  ...DEPRECATED_PAGES_ROUTES,

  // REDIRECT TO EXTERNAL URL
  {
    path: 'externalRedirect',
    canActivate: [ExternalRedirectGuard],
    children: [],
  },

  // WAITING URL
  {
    path: 'wait',
    children: [],
  },

  // COMPETITIONS SHORT INVITE
  {
    path: 'i/:id',
    redirectTo: shortInviteRedirector,
  },

  // NOT FOUND
  {
    path: '**',
    loadComponent: () => import('./pages/404/not-found.component').then(m => m.NotFoundComponent),
  },
] as const;

// Match guards run before route activation, including legacy link guards.
export const APP_ROUTES: Routes = APP_ROUTE_DEFINITIONS.map(route => ({
  ...route,
  canMatch: [RefreshTokenLinkGuard, ...(route.canMatch ?? [])],
}));
